import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Plus, Edit, Trash2, Bell, Calendar, Search, X, Save, AlertCircle, Info } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TeacherAnnouncements() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [announcements, setAnnouncements] = useState([]);
  const [courses, setCourses] = useState([]);
  const [showModal, setShowModal] = useState(searchParams.get('action') === 'add');
  const [editAnnouncement, setEditAnnouncement] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    title: '', message: '', course_id: '', priority: 'normal', expiry_date: ''
  });

  useEffect(() => { fetchData(); }, []);

  const getHeaders = () => {
    const token = localStorage.getItem('staffToken');
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('staffToken');
      if (!token) { navigate('/staff-portal'); return; }
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [announcementsRes, coursesRes] = await Promise.all([
        fetch(`${API_URL}/api/lms/announcements`, { headers }).then(r => r.json()).catch(() => []),
        fetch(`${API_URL}/api/lms/teacher/courses`, { headers }).then(r => r.json()).catch(() => [])
      ]);

      setAnnouncements(Array.isArray(announcementsRes) ? announcementsRes : []);
      setCourses(Array.isArray(coursesRes) ? coursesRes : []);
    } catch (error) {
      toast.error('Failed to load announcements');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.message) {
      toast.error('Please fill required fields');
      return;
    }

    setSaving(true);
    try {
      const url = editAnnouncement ? `${API_URL}/api/lms/announcements/${editAnnouncement.id}` : `${API_URL}/api/lms/announcements`;
      const response = await fetch(url, {
        method: editAnnouncement ? 'PUT' : 'POST',
        headers: getHeaders(),
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editAnnouncement ? 'Announcement updated!' : 'Announcement posted!');
        setShowModal(false);
        resetForm();
        fetchData();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save announcement');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    try {
      await fetch(`${API_URL}/api/lms/announcements/${id}`, { method: 'DELETE', headers: getHeaders() });
      toast.success('Announcement deleted!');
      fetchData();
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  const resetForm = () => {
    setFormData({ title: '', message: '', course_id: '', priority: 'normal', expiry_date: '' });
    setEditAnnouncement(null);
  };

  const openEdit = (announcement) => {
    setEditAnnouncement(announcement);
    setFormData({
      title: announcement.title || '', message: announcement.message || '',
      course_id: announcement.course_id || '', priority: announcement.priority || 'normal',
      expiry_date: announcement.expiry_date || ''
    });
    setShowModal(true);
  };

  const getPriorityStyle = (priority) => {
    if (priority === 'high') return { bg: 'bg-red-50 border-l-red-500', icon: AlertCircle, iconColor: 'text-red-600' };
    if (priority === 'low') return { bg: 'bg-gray-50 border-l-gray-400', icon: Info, iconColor: 'text-gray-500' };
    return { bg: 'bg-blue-50 border-l-blue-500', icon: Bell, iconColor: 'text-blue-600' };
  };

  if (loading) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-indigo-700 text-white">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/staff-portal/lms" className="p-2 hover:bg-indigo-600 rounded-lg"><ArrowLeft className="h-5 w-5" /></Link>
              <div><h1 className="text-xl font-bold">Announcements</h1><p className="text-indigo-200 text-sm">Post announcements to students</p></div>
            </div>
            <Button onClick={() => { resetForm(); setShowModal(true); }} className="bg-white text-indigo-700 hover:bg-indigo-50"><Plus className="h-4 w-4 mr-2" />Post Announcement</Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {announcements.length === 0 ? (
          <Card><CardContent className="p-12 text-center">
            <Bell className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">No announcements posted yet</p>
          </CardContent></Card>
        ) : (
          announcements.map(announcement => {
            const style = getPriorityStyle(announcement.priority);
            const Icon = style.icon;
            return (
              <Card key={announcement.id} className={`border-l-4 ${style.bg}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className={`p-2 bg-white rounded-lg ${style.iconColor}`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-gray-900">{announcement.title}</h3>
                          {announcement.priority === 'high' && (
                            <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">Important</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{announcement.message}</p>
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{announcement.created_at?.split('T')[0]}</span>
                          {announcement.course_name && <span>{announcement.course_name}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(announcement)}><Edit className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(announcement.id)} className="text-red-600"><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </main>

      {/* Post Announcement Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-lg">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold">{editAnnouncement ? 'Edit Announcement' : 'Post Announcement'}</h2>
              <button onClick={() => { setShowModal(false); resetForm(); }}><X className="h-5 w-5" /></button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div><Label>Title *</Label><Input value={formData.title} onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))} required /></div>
              
              <div><Label>Message *</Label><textarea value={formData.message} onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" rows={4} required /></div>
              
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Course (Optional)</Label>
                  <Select value={formData.course_id} onValueChange={(v) => setFormData(prev => ({ ...prev, course_id: v }))}>
                    <SelectTrigger><SelectValue placeholder="All Students" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">All Students</SelectItem>
                      {courses.map(c => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Priority</Label>
                  <Select value={formData.priority} onValueChange={(v) => setFormData(prev => ({ ...prev, priority: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="high">High (Important)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div><Label>Expiry Date (Optional)</Label><Input type="date" value={formData.expiry_date} onChange={(e) => setFormData(prev => ({ ...prev, expiry_date: e.target.value }))} /></div>
              
              <div className="flex gap-2 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => { setShowModal(false); resetForm(); }} className="flex-1">Cancel</Button>
                <Button type="submit" disabled={saving} className="flex-1">{saving ? 'Posting...' : <><Save className="h-4 w-4 mr-2" />{editAnnouncement ? 'Update' : 'Post'}</>}</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
